import { Flex, View } from '@adobe/react-spectrum';
import Button from '@react/react-spectrum/Button';
import ChevronLeft from '@spectrum-icons/workflow/ChevronLeft';
import ChevronRight from '@spectrum-icons/workflow/ChevronRight';
import p from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

/**
 * carousel item description
 * @memberof Carousel
 * @type {styled-component}
 */
const ItemDescription = styled.div`
  font-size: var(--spectrum-global-dimension-static-font-size-200);
  color: var(--spectrum-global-color-gray-800);
  padding: 24px;
  overflow-wrap: anywhere;
`;

/**
 * carousel item container
 * @memberof Carousel
 * @type {styled-component}
 */
const ItemContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  min-width: ${({ $isFullWidth, $visibleElements, $gap }) =>
    $isFullWidth
      ? '100%'
      : `calc(${100 / $visibleElements}% - ${($gap / $visibleElements) * ($visibleElements - 1)}px)`};
`;

/**
 * styled carousel next and previous buttons
 * @memberof Carousel
 * @type {styled-component}
 */
const PrevNextButton = styled(Button)`
  && {
    border-radius: 50%;
    z-index: 1;
  }
`;

/**
 * styled carousel items container
 * @memberof Carousel
 * @type {styled-component}
 */
const CarouselItemsContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-flow: row nowrap;
  overflow: hidden;
  border-radius: 4px 4px 0 0;
  border-color: #eaeaea;
  border: 1px;
  z-index: 0;
`;

/**
 * container with transitions for changing carousel items
 * @memberof Carousel
 * @type {styled-component}
 */
const CarouselTransitionContainer = styled.div`
  display: flex;
  transform: translateX(0);
  transition: 0.25s;
  gap: ${({ $gap }) => $gap}px;
  width: 100%;
`;

/**
 * dot indicator for the carousel
 * @memberof Carousel
 * @type {styled-component}
 */
const CarouselDotIndicator = styled.div`
  height: 8px;
  width: 8px;
  background-color: ${(props) =>
    props.selected ? 'var(--spectrum-global-color-gray-800)' : 'var(--spectrum-global-color-gray-400)'};
  border-radius: 50%;
  display: inline-block;
  margin-left: 7px;
  transition: 0.25s;
`;

/**
 * styled fieldset
 * @memberof Carousel
 * @type {styled-component}
 */
const CarouselFieldset = styled.fieldset`
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
`;

/**
 * Accessible carousel component. Children should be `CarouselItem`s
 */
const Carousel = (props) => {
  const {
    captions,
    carouselPosition,
    fullWidthElements = true,
    visibleElements = 1,
    gap = 0,
    hideDots = false,
    children = [],
    ...rest
  } = props;
  const items = [].concat(children);
  const firstElementKey = items[0]?.key;
  const elementsToShow = fullWidthElements ? 1 : items.length >= visibleElements ? visibleElements : items.length;
  const elementsGap = fullWidthElements ? 0 : gap;
  const [swipeStart, setSwipeStart] = useState(0);

  const translateValues = useMemo(
    () =>
      Array(items.length - elementsToShow + 1)
        .fill(0)
        .map((_, idx) => {
          if (idx === 0) {
            return '0px';
          } else {
            return `calc(-${(100 / elementsToShow) * idx}% - ${(elementsGap * idx) / elementsToShow}px)`;
          }
        }),
    [items.length, elementsGap, elementsToShow]
  );
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    if (items.length > 0) {
      if (carouselPosition) {
        const parsedCarouselPosition = parseInt(carouselPosition);
        setCarouselIndex(parsedCarouselPosition);
      } else {
        setCarouselIndex(0);
      }
    }
  }, [firstElementKey, items.length, carouselPosition]);

  const handleSwipeStart = (event) => {
    setSwipeStart(event.targetTouches[0]?.clientX);
  };

  const handleSwipeEnd = (event) => {
    const swipeEnd = event.changedTouches[0]?.clientX;
    if (swipeEnd - swipeStart > 50 && carouselIndex > 0) {
      setCarouselIndex(carouselIndex - 1);
    } else if (swipeEnd - swipeStart < 50 && carouselIndex < items?.length - 1) {
      setCarouselIndex(carouselIndex + 1);
    }
  };

  return (
    <View {...rest}>
      <View
        marginBottom="size-200"
        backgroundColor={!fullWidthElements && visibleElements > 1 ? 'transparent' : 'static-white'}
        borderRadius="regular"
        borderWidth={!fullWidthElements && visibleElements > 1 ? null : 'thin'}
        borderColor="gray-200"
        width="100%"
        id="carousel"
      >
        <Flex direction="column" height="100%">
          <ItemContainer $isFullWidth={true}>
            <CarouselItemsContainer>
              <CarouselTransitionContainer
                data-testid="carousel-transition-container"
                $gap={elementsGap}
                style={{
                  transform: `translateX(${translateValues[carouselIndex]})`,
                }}
              >
                {items.length > 0 ? (
                  items.map((child, index) =>
                    React.cloneElement(child, {
                      onTouchStart: handleSwipeStart,
                      onTouchEnd: handleSwipeEnd,
                      ariaHidden: carouselIndex !== index ? 'true' : undefined,
                      $isFullWidth: fullWidthElements,
                      $visibleElements: elementsToShow,
                      $gap: elementsGap,
                    })
                  )
                ) : (
                  <Flex justifyContent="center" alignItems="center" width="100%">
                    ERR: Carousel did NOT receive any items
                  </Flex>
                )}
              </CarouselTransitionContainer>
            </CarouselItemsContainer>
            {/* Fieldset for better a11y */}
            <CarouselFieldset aria-label="carousel buttons" aria-controls="carousel">
              {carouselIndex > 0 && (
                <PrevNextButton
                  data-testid="previous-item"
                  data-launchid="previous-item"
                  style={{ position: 'absolute', top: 'calc(50% - 16px)', left: '-16px' }}
                  variant="action"
                  icon={<ChevronLeft aria-label="previous item" />}
                  onClick={() => setCarouselIndex(carouselIndex - 1)}
                  aria-label="previous"
                />
              )}
              {carouselIndex < translateValues.length - 1 && (
                <PrevNextButton
                  data-testid="next-item"
                  data-launchid="next-item"
                  style={{ position: 'absolute', top: 'calc(50% - 16px)', right: '-16px' }}
                  variant="action"
                  icon={<ChevronRight aria-label="next item" />}
                  onClick={() => setCarouselIndex(carouselIndex + 1)}
                  aria-label="next"
                />
              )}
            </CarouselFieldset>
          </ItemContainer>
          {captions?.[carouselIndex] && (
            <View>
              <ItemDescription>{captions[carouselIndex] ?? ''}</ItemDescription>
            </View>
          )}
        </Flex>
      </View>
      <Flex aria-hidden="true" alignItems="center" justifyContent="center">
        {!hideDots &&
          items.length > 1 &&
          translateValues.map((_item, index) => (
            <CarouselDotIndicator key={`dot-indicator-${index}`} selected={index === carouselIndex} />
          ))}
      </Flex>
    </View>
  );
};

Carousel.propTypes = {
  /* An array of captions for the elements. Usually used with images. Please don't use it when visibleElements > 1 */
  captions: p.arrayOf(p.string),
  carouselPosition: p.string,
  fullWidthElements: p.bool,
  visibleElements: p.number,
  gap: p.number,
  hideDots: p.bool,
};

Carousel.defaultProps = {
  fullWidthElements: true,
  visibleElements: 1,
  gap: 0,
  hideDots: false,
  children: [],
};

export default Carousel;
export const CarouselItem = ItemContainer;
